import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./AppManager.scss";
import { VersionPageProps, VersionType } from "../../../types/app/versionTypes";
import { useEffect, useState } from "react";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { LoadingItem } from "../../../shared/components/loading";
import VersionsTable from "./VersionsTable";
import { FaApple, FaAndroid } from "react-icons/fa";


const validationSchema = Yup.object({
    platform: Yup.string().required("Platform is required"),
    buildNumber: Yup.number()
        .typeError("Build number must be a number")
        .integer("Build number must be an integer")
        .positive("Build number must be greater than 0")
        .required("Build number is required"),

    minimumSupportedVersion: Yup.string()
        .matches(
            /^\d+(\.\d+)*$/,
            "Minimum Version must be numbers separated by dots (e.g. 1.0.3)"
        )
        .required("Minimum version is required"),
    version: Yup.string()
        .matches(
            /^\d+(\.\d+)*$/,
            "Version must be numbers separated by dots (e.g. 1.0.3)"
        )
        .required("version is required"),
    releaseDate: Yup.string().required("Release date is required"),
    effectiveDate: Yup.string().required("Effective date is required"),
    releaseNotes: Yup.string().required("Release notes are required"),
    updateUrl: Yup.string().url("Invalid URL").required("Update URL is required"),
});

interface ActionProps {
    pageProps: VersionPageProps;
    payload: VersionType | "CLEAR" | {
        platform: 'ios' | 'android'
    }
}


const updateVersionAction = async ({ pageProps, payload }: ActionProps) => {
    await pageProps.updateVersionAction(payload as VersionType | "CLEAR")
}

const createVersionAction = async ({ pageProps, payload }: ActionProps) => {
    await pageProps.createVersionAction(payload as VersionType | "CLEAR")
}
const getVersionsAction = async ({ pageProps, payload }: ActionProps) => {
    await pageProps.getVersionsAction(payload as {
        platform: 'ios' | 'android'
    } | "CLEAR")
}


const modules = {
    toolbar: [
        [{ size: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["image", "link"],
    ],
};

const formats = [
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "align",
    "list",
    "bullet",
    "image",
    "link",
];

const AppManager = ({ pageProps }: { pageProps: VersionPageProps }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [mode, setMode] = useState<'view' | 'edit' | 'add'>('view');
    const [platform, setPlatform] = useState<"ios" | "android">("android")
    const [versionsData, setVersionsData] = useState<VersionType[]>([]);
    const [versionEditValues, setVersionEditValues] = useState<VersionType>({
        platform: "android",
        buildNumber: 0,
        minimumSupportedVersion: "",
        releaseDate: "",
        effectiveDate: "",
        forceUpdate: false,
        isCritical: false,
        releaseNotes: "",
        updateUrl: "",
        version: "",
    });

    const [versionAddValues, setVersionAddValues] = useState<VersionType>({
        platform: "android",
        buildNumber: 0,
        minimumSupportedVersion: "",
        releaseDate: "",
        effectiveDate: "",
        forceUpdate: false,
        isCritical: false,
        releaseNotes: "",
        updateUrl: "",
        version: "",
    });

    useEffect(() => {
        updateVersionAction({
            pageProps,
            payload: "CLEAR"
        })

        createVersionAction({
            pageProps,
            payload: "CLEAR"
        })

        getVersionsAction({
            pageProps,
            payload: { platform: platform }
        })
    }, [])

    useEffect(() => {
        getVersionsAction({
            pageProps,
            payload: { platform: platform }
        })
    }, [platform])


    useEffect(() => {
        if (pageProps?.getVersionsRequest?.request_status === "GET_VERSIONS_SUCCESS") {
            setVersionsData(prev => [...prev, ...pageProps?.getVersionsRequest?.request_data?.response])

        } else {
            setVersionsData(() => [])
        }
    }, [pageProps?.getVersionsRequest?.request_status])

    const handleEdit = (data: VersionType) => {
        setVersionEditValues(data)
        setMode('edit')
    }

    const handleupdateVersion = (values: VersionType) => {
        setIsLoading(true)
        const payload: VersionType = {
            versionId: values.id,
            platform: values.platform,
            version: values.version,
            buildNumber: values.buildNumber,
            isCritical: values.isCritical,
            forceUpdate: values.forceUpdate,
            minimumSupportedVersion: values.minimumSupportedVersion,
            updateUrl: values.updateUrl,
            releaseNotes: values.releaseNotes,
            releaseDate: new Date(values.releaseDate).toISOString(),
            effectiveDate: new Date(values.effectiveDate).toISOString()
        }

        updateVersionAction({ pageProps, payload }).then(() => setMode('view')).finally(() => {
            setIsLoading(false)
            getVersionsAction({
                pageProps,
                payload: { platform: platform }
            })
        });
    };

    const handleAdd = () => {
        setMode('add')
    }

    const handleCreateVersion = (values: VersionType) => {
        setIsLoading(true)
        const payload: VersionType = {
            versionId: values.id,
            platform: values.platform,
            version: values.version,
            buildNumber: values.buildNumber,
            isCritical: values.isCritical,
            forceUpdate: values.forceUpdate,
            minimumSupportedVersion: values.minimumSupportedVersion,
            updateUrl: values.updateUrl,
            releaseNotes: values.releaseNotes,
            releaseDate: new Date(values.releaseDate).toISOString(),
            effectiveDate: new Date(values.effectiveDate).toISOString()
        }

        createVersionAction({ pageProps, payload }).then(() => setMode('view')).finally(() => {
            setIsLoading(false)
            getVersionsAction({
                pageProps,
                payload: { platform: platform }
            })
        });
    };


    return (
        <div className="app-manager">
            <div className="title-row">
                <div className="title-col">
                    <h2 className="title">App Configuration</h2>
                    {mode !== 'add' &&
                        <button className="add-btn" onClick={handleAdd}>
                            Add Version
                        </button>
                    }
                </div>
                {mode === 'view' &&
                    < div className="platform-switch">
                        <div
                            className={`active-pill ${platform === "ios" ? "left" : "right"
                                }`}
                        />

                        <button
                            className={`platform-btn ${platform === "ios" ? "active" : ""
                                }`}
                            onClick={() => setPlatform("ios")}
                        >
                            <FaApple />
                            <span>Apple</span>
                        </button>

                        <button
                            className={`platform-btn ${platform === "android" ? "active" : ""
                                }`}
                            onClick={() => setPlatform("android")}
                        >
                            <FaAndroid />
                            <span>Android</span>
                        </button>
                    </div>
                }
            </div>
            <br />
            {pageProps?.getVersionsRequest?.request_status === "GET_VERSIONS_FAILURE" && <AlertMsg type="error" message={pageProps?.getVersionsRequest?.request_data?.error} />}
            {pageProps?.updateVersionRequest?.request_status === "UPDATE_VERSION_FAILURE" && <AlertMsg type="error" message={pageProps?.updateVersionRequest?.request_data?.error} />}
            {pageProps?.createVersionRequest?.request_status === "CREATE_VERSION_FAILURE" && <AlertMsg type="error" message={pageProps?.updateVersionRequest?.request_data?.error} />}
            {mode === 'view' &&
                !pageProps?.getVersionsRequest?.is_request_processing ? (<VersionsTable versionsData={versionsData} handleEdit={handleEdit} />) :
                pageProps?.getVersionsRequest?.is_request_processing ? (<LoadingItem />) : null
            }
            {mode === 'edit' &&
                <>

                    {pageProps?.updateVersionRequest?.request_status === "UPDATE_VERSION_SUCCESS" && <AlertMsg type="success" message={'Updated Successfully'} />}
                    {isLoading && <LoadingItem />}
                    <Formik
                        initialValues={versionEditValues}
                        validationSchema={validationSchema}
                        onSubmit={handleupdateVersion}
                    >
                        {() => (
                            <Form className="form-grid">
                                <div className="form-main">

                                    <div className="form-group">
                                        <label>Platform</label>
                                        <Field as="select" name="platform">
                                            <option value="">Select platform</option>
                                            <option value="ios">iOS</option>
                                            <option value="android">Android</option>
                                        </Field>
                                        <ErrorMessage name="platform" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Build Number</label>
                                        <Field name="buildNumber" type="number" />
                                        <ErrorMessage name="buildNumber" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Current Version</label>
                                        <Field name="minimumSupportedVersion" placeholder="e.g. 1.0.5" />
                                        <ErrorMessage name="minimumSupportedVersion" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Latest Version</label>
                                        <Field name="version" placeholder="e.g. 1.0.6" />
                                        <ErrorMessage name="version" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Release Date</label>
                                        <Field type="datetime-local" name="releaseDate" />
                                        <ErrorMessage name="releaseDate" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Effective Date</label>
                                        <Field type="datetime-local" name="effectiveDate" />
                                        <ErrorMessage name="effectiveDate" component="span" className="error" />
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Update URL</label>
                                        <Field name="updateUrl" />
                                        <ErrorMessage name="updateUrl" component="span" className="error" />
                                    </div>
                                </div>

                                {/* RIGHT SIDE */}
                                <div className="form-side">

                                    <div className="form-group full-width">
                                        <label>Release Notes</label>
                                        <Field name="releaseNotes">
                                            {({ field, form }: any) => (
                                                <ReactQuill
                                                    value={field.value}
                                                    onChange={(val) => form.setFieldValue(field.name, val)}
                                                    className="custom-quill"
                                                    modules={modules}
                                                    formats={formats}
                                                />
                                            )}
                                        </Field>
                                        <ErrorMessage name="releaseNotes" component="span" className="error" />
                                    </div>

                                    <div className="toggle-group vertical">
                                        {/* Force Update */}
                                        <div className="toggle-wrapper">
                                            <span className="toggle-label">Force Update</span>
                                            <Field name="forceUpdate">
                                                {({ field, form }: any) => (

                                                    <button
                                                        type="button"
                                                        className={`toggle ${field.value ? "active" : ""}`}
                                                        onClick={() => form.setFieldValue(field.name, !field.value)}
                                                        aria-pressed={field.value}
                                                    >
                                                        <span className="toggle-thumb">
                                                            <svg
                                                                width="30"
                                                                height="33"
                                                                viewBox="0 0 30 33"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <g filter="url(#filter0_d)">
                                                                    <rect
                                                                        x="4"
                                                                        y="4"
                                                                        width="21.0843"
                                                                        height="24.3902"
                                                                        rx="10.5422"
                                                                        fill="white"
                                                                    />
                                                                    <path
                                                                        d="M19.0742 12.9297L12.8867 18.9297L10.0742 16.2024"
                                                                        stroke={`${field.value ? "#00A759" : "black"}`}
                                                                        strokeWidth="1.3"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        </span>
                                                    </button>
                                                )}
                                            </Field>
                                        </div>

                                        {/* Critical Update */}
                                        <div className="toggle-wrapper">
                                            <span className="toggle-label">Critical Update</span>
                                            <Field name="isCritical">
                                                {({ field, form }: any) => (

                                                    <button
                                                        type="button"
                                                        className={`toggle ${field.value ? "active" : ""}`}
                                                        onClick={() => form.setFieldValue(field.name, !field.value)}
                                                        aria-pressed={field.value}
                                                    >
                                                        <span className="toggle-thumb">
                                                            <svg
                                                                width="30"
                                                                height="33"
                                                                viewBox="0 0 30 33"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <g filter="url(#filter0_d)">
                                                                    <rect
                                                                        x="4"
                                                                        y="4"
                                                                        width="21.0843"
                                                                        height="24.3902"
                                                                        rx="10.5422"
                                                                        fill="white"
                                                                    />
                                                                    <path
                                                                        d="M19.0742 12.9297L12.8867 18.9297L10.0742 16.2024"
                                                                        stroke={`${field.value ? "#00A759" : "black"}`}
                                                                        strokeWidth="1.3"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        </span>
                                                    </button>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                </div>

                                {/* ACTIONS */}
                                <div className="actions">
                                    <button type="submit" className="submit-btn">Publish Update</button>
                                    <button className="cancel-btn" type="button" onClick={() => setMode('view')}>Cancel Update</button>
                                </div>
                            </Form>

                        )}
                    </Formik>
                </>
            }
            {mode === 'add' &&
                <>

                    {pageProps?.createVersionRequest?.request_status === "CREATE_VERSION_SUCCESS" && <AlertMsg type="success" message={'Updated Successfully'} />}
                    {isLoading && <LoadingItem />}
                    <Formik
                        initialValues={versionAddValues}
                        validationSchema={validationSchema}
                        onSubmit={handleCreateVersion}
                    >
                        {() => (
                            <Form className="form-grid">
                                <div className="form-main">

                                    <div className="form-group">
                                        <label>Platform</label>
                                        <Field as="select" name="platform">
                                            <option value="">Select platform</option>
                                            <option value="ios">iOS</option>
                                            <option value="android">Android</option>
                                        </Field>
                                        <ErrorMessage name="platform" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Build Number</label>
                                        <Field name="buildNumber" type="number" />
                                        <ErrorMessage name="buildNumber" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Current Version</label>
                                        <Field name="minimumSupportedVersion" placeholder="e.g. 1.0.5" />
                                        <ErrorMessage name="minimumSupportedVersion" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Latest Version</label>
                                        <Field name="version" placeholder="e.g. 1.0.6" />
                                        <ErrorMessage name="version" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Release Date</label>
                                        <Field type="datetime-local" name="releaseDate" />
                                        <ErrorMessage name="releaseDate" component="span" className="error" />
                                    </div>

                                    <div className="form-group">
                                        <label>Effective Date</label>
                                        <Field type="datetime-local" name="effectiveDate" />
                                        <ErrorMessage name="effectiveDate" component="span" className="error" />
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Update URL</label>
                                        <Field name="updateUrl" />
                                        <ErrorMessage name="updateUrl" component="span" className="error" />
                                    </div>
                                </div>

                                {/* RIGHT SIDE */}
                                <div className="form-side">

                                    <div className="form-group full-width">
                                        <label>Release Notes</label>
                                        <Field name="releaseNotes">
                                            {({ field, form }: any) => (
                                                <ReactQuill
                                                    value={field.value}
                                                    onChange={(val) => form.setFieldValue(field.name, val)}
                                                    className="custom-quill"
                                                    modules={modules}
                                                    formats={formats}
                                                />
                                            )}
                                        </Field>
                                        <ErrorMessage name="releaseNotes" component="span" className="error" />
                                    </div>

                                    <div className="toggle-group vertical">
                                        {/* Force Update */}
                                        <div className="toggle-wrapper">
                                            <span className="toggle-label">Force Update</span>
                                            <Field name="forceUpdate">
                                                {({ field, form }: any) => (

                                                    <button
                                                        type="button"
                                                        className={`toggle ${field.value ? "active" : ""}`}
                                                        onClick={() => form.setFieldValue(field.name, !field.value)}
                                                        aria-pressed={field.value}
                                                    >
                                                        <span className="toggle-thumb">
                                                            <svg
                                                                width="30"
                                                                height="33"
                                                                viewBox="0 0 30 33"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <g filter="url(#filter0_d)">
                                                                    <rect
                                                                        x="4"
                                                                        y="4"
                                                                        width="21.0843"
                                                                        height="24.3902"
                                                                        rx="10.5422"
                                                                        fill="white"
                                                                    />
                                                                    <path
                                                                        d="M19.0742 12.9297L12.8867 18.9297L10.0742 16.2024"
                                                                        stroke={`${field.value ? "#00A759" : "black"}`}
                                                                        strokeWidth="1.3"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        </span>
                                                    </button>
                                                )}
                                            </Field>
                                        </div>

                                        {/* Critical Update */}
                                        <div className="toggle-wrapper">
                                            <span className="toggle-label">Critical Update</span>
                                            <Field name="isCritical">
                                                {({ field, form }: any) => (

                                                    <button
                                                        type="button"
                                                        className={`toggle ${field.value ? "active" : ""}`}
                                                        onClick={() => form.setFieldValue(field.name, !field.value)}
                                                        aria-pressed={field.value}
                                                    >
                                                        <span className="toggle-thumb">
                                                            <svg
                                                                width="30"
                                                                height="33"
                                                                viewBox="0 0 30 33"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <g filter="url(#filter0_d)">
                                                                    <rect
                                                                        x="4"
                                                                        y="4"
                                                                        width="21.0843"
                                                                        height="24.3902"
                                                                        rx="10.5422"
                                                                        fill="white"
                                                                    />
                                                                    <path
                                                                        d="M19.0742 12.9297L12.8867 18.9297L10.0742 16.2024"
                                                                        stroke={`${field.value ? "#00A759" : "black"}`}
                                                                        strokeWidth="1.3"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        </span>
                                                    </button>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                </div>

                                {/* ACTIONS */}
                                <div className="actions">
                                    <button type="submit" className="submit-btn">Publish Update</button>
                                    <button className="cancel-btn" type="button" onClick={() => setMode('view')}>Cancel Update</button>
                                </div>
                            </Form>

                        )}
                    </Formik>
                </>
            }
        </div>
    );
};

export default AppManager;
